import { chunk, compact, flatten, head, uniq } from 'lodash';

import {
  CoinListing,
  SimplePriceResponse,
  toInterfaceBySymbol,
  MarketChartRange,
  MarketCoin,
} from 'app/types/coingecko';
import { TimeRange, PriceSymbol, CoinPrices } from 'app/types/interface';
import { WebRequestService } from 'app/services/web_request_service';
import { chunkDelay } from 'app/utils';

/**
 * coingecko rejects the whole request with a 400 if `symbols` carries more than
 * 50 entries, so anything larger has to be split across several calls.
 */
const maxSymbolsPerRequest = 50;

/** how many of those calls we let run at once, to stay under the rate limit */
const concurrentRequests = 2;

// No interval means coingecko picks one from the range: every 5 minutes up to a day. The free plan rejects 'minutely'.
const timeRangeToDays: Record<TimeRange, { days: number; interval?: string }> = {
  hour: { days: 1 / 24 },
  '6 hours': { days: 6 / 24 },
  day: { days: 1 },
  week: { days: 7, interval: 'hourly' },
  '2 weeks': { days: 7 * 2, interval: 'hourly' },
  month: { days: 31, interval: 'daily' },
  '3 months': { days: 31 * 3, interval: 'daily' },
  '6 months': { days: 31 * 6, interval: 'daily' },
  year: { days: 365, interval: 'daily' },
};

export class CoinGeckoService extends WebRequestService<Array<unknown> | unknown> {
  baseUrl: string;

  // the ranges the time bar offers
  static timeRanges: Array<TimeRange> = ['hour', 'day', 'week', 'month', 'year'];

  private symbolToInfo: Record<string, { id: string; name: string }> = {};

  /**
   * Many coins share a symbol (there are dozens of "eth"s), and the coin list keeps whichever came last. The markets
   * endpoint answers with the biggest coin for each symbol, so ids learned from it win.
   */
  private symbolToMarketId: Record<string, string> = {};

  constructor() {
    super();
    // proxied through our backend so the responses can be cached; coingecko's keyless
    // api starts answering 429 after a handful of calls a minute
    this.baseUrl = 'api/v1/proxy_coingecko/';
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
  }

  async getCoinList(): Promise<Array<CoinListing>> {
    const result: Array<CoinListing> = await this.getJson('coin_list');
    result.forEach(({ id, symbol, name }) => {
      if (!name.match('\\[OLD\\]')) this.symbolToInfo[symbol] = { id, name };
    });

    return result;
  }

  async getPriceHistory(coin: string, timeRange: TimeRange = 'day'): Promise<MarketChartRange | undefined> {
    // the symbol -> id map only exists once the catalogue has loaded, and expanding a
    // row can beat that; without this the call quietly resolves to undefined
    const symbol = coin.toLowerCase();
    if (!this.symbolToMarketId[symbol]) {
      try {
        await this.getMarkets([symbol]);
      } catch (e) {
        console.warn(`coingecko markets unavailable for ${coin}`, e);
      }
    }
    if (!this.symbolToMarketId[symbol] && Object.keys(this.symbolToInfo).length === 0) await this.getCoinList();

    const id = this.symbolToMarketId[symbol] ?? this.symbolToInfo[symbol]?.id;
    if (id) {
      const { days, interval } = timeRangeToDays[timeRange];
      const result = await this.getJson<MarketChartRange>(`market_chart/${id}`, { days, interval });

      return result;
    } else return;
  }

  /**
   * Every coingecko endpoint that takes `symbols` caps it at 50, so a long coin
   * list has to be split across several calls. A batch that fails is dropped
   * rather than costing us the batches that succeeded; we only give up when
   * nothing at all came back.
   */
  private async fetchInSymbolBatches<Result>(
    coins: Array<string>,
    request: (symbols: string) => Promise<Result>,
    label: string
  ): Promise<Array<Result>> {
    // sorted so a given portfolio always produces the same batches, and therefore
    // always hits the same backend cache entries
    const batches = chunk(uniq(coins.map(c => c.toLowerCase())).sort(), maxSymbolsPerRequest);
    if (batches.length === 0) return [];

    const results = await chunkDelay(
      batches,
      concurrentRequests
    )(async (batch: Array<string>): Promise<Result | undefined> => {
      try {
        return await request(batch.join(','));
      } catch (e) {
        console.warn(`coingecko ${label} batch failed for ${batch.length} symbols`, e);
        return undefined;
      }
    });

    const successful = compact(results);
    if (successful.length === 0) throw new Error(`every coingecko ${label} request failed`);

    return successful;
  }

  async getPricesBySymbol(coins: Array<string>, currencies: Array<PriceSymbol> = ['USD', 'BTC']): Promise<CoinPrices> {
    const results = await this.fetchInSymbolBatches<SimplePriceResponse<PriceSymbol>>(
      coins,
      symbols => this.getJson('prices', { symbols, vs_currencies: currencies.join(',') }),
      'price'
    );

    return Object.assign({}, ...results.map(toInterfaceBySymbol));
  }

  /**
   * coins/markets is the cheapest endpoint that carries an icon url, which we
   * need since cryptocompare's coinlist now demands an api key.
   */
  async getMarkets(coins: Array<string>): Promise<Array<MarketCoin>> {
    const results = await this.fetchInSymbolBatches<Array<MarketCoin>>(
      coins,
      symbols => this.getJson('markets', { symbols }),
      'markets'
    );

    const markets = flatten(results);
    markets.forEach(({ id, symbol }) => {
      this.symbolToMarketId[symbol.toLowerCase()] = id;
    });

    return markets;
  }

  // our api wraps every payload in { data: [...] }; the proxied coingecko response
  // is the single element inside it
  extractJsonData(json: Array<unknown> | unknown) {
    const { data } = json as { data?: Array<unknown> };
    return data ? head(data) : json;
  }
}
